import React from 'react';
import { useLocation } from 'react-router-dom';
import { Nav } from 'react-bootstrap';
import { NavLink } from '../../../../Router';
import { NavLinkIcon } from '../../../atoms/navigation/NavLinkIcon';
import { NavLinkLabel } from '../../../atoms/navigation/NavLinkLabel';
import classNames from 'classnames';
import { MenuItemType } from '../types';
import { NavItemWithSubItems } from '../NavItemWithSubItems/NavItemWithSubItems';
import './NavItem.css';

export type NavItemProps = {
  menuItem: MenuItemType;
  //Navigational Item is each item that will navigate to a new route
  onNavigationalItemClick?: () => void;
};

const isPathnameActive = (pathname: string, routeTemplate: string): boolean =>
  pathname === routeTemplate ||
  new RegExp(
    `^${routeTemplate.replace(/\//g, '\\/').replace(/:[\w]+/g, '[^/]+')}$`,
  ).test(pathname);

export const NavItem: React.FC<React.PropsWithChildren<NavItemProps>> = ({
  menuItem,
  onNavigationalItemClick,
}) => {
  const { pathname } = useLocation();
  const route = menuItem.route;
  const isActive = Boolean(
    route &&
      (menuItem.routeParams
        ? pathname === route.create(menuItem.routeParams).split('?')[0]
        : isPathnameActive(pathname, route.template())),
  );

  if (!menuItem.shouldRender) {
    return null;
  }

  if (menuItem.subItems) {
    return (
      <NavItemWithSubItems
        menuItem={menuItem}
        onSubNavItemClick={onNavigationalItemClick}
      />
    );
  }

  if (menuItem.onClick) {
    return (
      <Nav.Item>
        <button
          type="button"
          onClick={() => {
            menuItem.onClick?.();
            onNavigationalItemClick?.();
          }}
          className={classNames(
            'nav-link',
            'd-flex',
            'align-items-center',
            'nav-link--button',
          )}>
          <NavLinkIcon isActive={false}>{menuItem.icon}</NavLinkIcon>
          <NavLinkLabel data-testid={menuItem.label}>
            {menuItem.label}
          </NavLinkLabel>
          {menuItem.suffix}
        </button>
      </Nav.Item>
    );
  }

  return (
    <Nav.Item onClick={onNavigationalItemClick}>
      <NavLink
        exact={menuItem.route?.template() === '/'}
        to={menuItem.route}
        {...(menuItem.routeParams || {})}
        className={classNames('nav-link', 'd-flex', 'align-items-center')}>
        <NavLinkIcon isActive={isActive}>
          {isActive ? menuItem.activeIcon : menuItem.icon}
        </NavLinkIcon>
        <NavLinkLabel data-testid={menuItem.label}>
          {menuItem.label}
        </NavLinkLabel>
        {menuItem.suffix}
      </NavLink>
    </Nav.Item>
  );
};
